// Screen-reader-first "monitor as text" mode.
// Renders the vitals as a hidden aria-live region that the screen reader announces every
// announcementMs (default 10s). Visually-impaired learners can train without sight.
// Also exposes a "narrate now" hotkey: pressing N at any time forces an immediate announcement.

import { useEffect, useRef, useState } from 'react';
import type { MonitorState } from './MonitorPanel';

const RHYTHM_SPOKEN: Record<string, string> = {
  sinus: 'sinus rhythm',
  sinus_tach: 'sinus tachycardia',
  brady: 'sinus bradycardia',
  af: 'atrial fibrillation',
  aflutter: 'atrial flutter',
  svt: 'supraventricular tachycardia',
  vt_pulse: 'ventricular tachycardia with pulse',
  vt_pulseless: 'pulseless ventricular tachycardia, shockable',
  vf: 'ventricular fibrillation, shockable',
  torsades: 'torsades de pointes, shockable',
  asystole: 'asystole, non-shockable',
  p_wave_asystole: 'P-wave asystole, non-shockable',
  pea_narrow: 'pulseless electrical activity, narrow complex',
  pea_broad: 'pulseless electrical activity, broad complex',
  av1: 'first-degree atrioventricular block',
  chb: 'complete heart block',
  idioventricular: 'idioventricular rhythm',
  agonal: 'agonal rhythm',
};

interface Props {
  state: MonitorState;
  /** Announce every N ms. Default 10 000. */
  announcementMs?: number;
  /** Listen for the "N" key globally to force an immediate announcement. */
  enableHotkey?: boolean;
  /** Visual mode (visible in the DOM); default false = sr-only. */
  visible?: boolean;
}

function compose(state: MonitorState): string {
  const rhythm = RHYTHM_SPOKEN[state.rhythm] || state.rhythm;
  const hr = state.hr > 0 ? `Heart rate ${state.hr}` : 'No heart rate detected';
  const bp = state.sbp > 0 ? `Blood pressure ${state.sbp} over ${state.dbp}` : 'No blood pressure';
  const sp = state.spo2 > 0 ? `Oxygen saturation ${state.spo2} percent` : 'Oxygen saturation unreadable';
  const co2 = state.etco2_mmhg > 0 ? `End-tidal CO2 ${state.etco2_mmhg.toFixed(0)} millimetres of mercury` : 'No end-tidal CO2';
  const cpr = state.cpr_active ? `CPR active, quality ${state.cpr_quality_pct} percent` : '';
  return [
    `Rhythm: ${rhythm}.`,
    `${hr}.`,
    `${bp}.`,
    `${sp}.`,
    `${co2}.`,
    cpr ? `${cpr}.` : '',
    `Time since arrest start ${(state.sim_t_ms / 1000).toFixed(0)} seconds.`,
  ].filter(Boolean).join(' ');
}

export default function MonitorAsText({ state, announcementMs = 10_000, enableHotkey = true, visible = false }: Props) {
  const [text, setText] = useState<string>(() => compose(state));
  const stateRef = useRef(state);
  stateRef.current = state;

  // Periodic announcement
  useEffect(() => {
    const id = window.setInterval(() => {
      setText(compose(stateRef.current));
    }, announcementMs);
    return () => window.clearInterval(id);
  }, [announcementMs]);

  // Re-announce immediately when rhythm or phase changes (those are important state shifts)
  useEffect(() => {
    setText(compose(state));
  }, [state.rhythm]);

  // Manual narrate hotkey
  useEffect(() => {
    if (!enableHotkey) return;
    function onKey(e: KeyboardEvent) {
      const tag = (e.target as HTMLElement | null)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;
      if (e.key === 'n' || e.key === 'N') {
        // Bump the text by appending a trailing space to force re-announcement even if the value matches
        setText((prev) => compose(stateRef.current) + (prev.endsWith(' ') ? '' : ' '));
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [enableHotkey]);

  return (
    <>
      <div
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className={visible ? 'panel p-3 text-sm font-mono text-mono-white/85' : 'sr-only'}
        data-testid="monitor-as-text"
      >
        {text}
      </div>
      {!visible && (
        <p className="sr-only">Press <kbd>N</kbd> at any time to re-announce the current vitals.</p>
      )}
    </>
  );
}
